import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../auth-context";
import { api } from "../api";
import { Logo } from "./Logo";
import { TopBar } from "./TopBar";

const nav = [
  { to: "/", label: "Overview", exact: true, icon: "▦" },
  { to: "/events", label: "Events", icon: "≡" },
  { to: "/policies", label: "Policies", icon: "⎚" },
  { to: "/protections", label: "Protections", icon: "⊞" },
  { to: "/users", label: "Users", icon: "◈" },
  { to: "/api-access", label: "API Access", icon: "◉" },
  { to: "/billing", label: "Billing", icon: "£" },
  { to: "/settings", label: "Settings", icon: "⚙" },
];

function roleLabel(role?: string): string {
  switch (role) {
    case "super_admin": return "Super Admin";
    case "workspace_admin": return "Workspace Admin";
    case "team_manager": return "Team Manager";
    case "demo_viewer": return "Demo (Read-Only)";
    default: return "Member";
  }
}

function displayNameFromEmail(email?: string | null): string {
  if (!email) return "User";
  const local = email.split("@")[0] ?? email;
  return local
    .split(/[.\-_+]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ") || "User";
}

export function Shell() {
  const { profile, isDemo } = useAuth();
  const navigate = useNavigate();
  const username = displayNameFromEmail(profile.user?.email);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="sg-app">
      {/* Mobile top bar — hidden on desktop via CSS */}
      <div className="sg-mobile-bar">
        <button
          className="sg-hamburger"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open navigation"
        >
          <span /><span /><span />
        </button>
        <div className="sg-mobile-brand">
          <Logo size={20} />
          <span className="sg-brand-name">Ventrin</span>
        </div>
      </div>

      {/* Backdrop — taps outside close the sidebar on mobile */}
      {sidebarOpen && (
        <div className="sg-sidebar-backdrop" onClick={closeSidebar} aria-hidden />
      )}

      <aside className={`sg-sidebar${sidebarOpen ? " is-open" : ""}`}>
        <div className="sg-brand">
          <Logo size={22} />
          <div className="sg-brand-name">Ventrin</div>
          {/* Close button — visible only on mobile */}
          <button
            className="sg-sidebar-close"
            onClick={closeSidebar}
            aria-label="Close navigation"
          >✕</button>
        </div>
        <nav className="sg-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeSidebar}
            >
              <span className="sg-nav-icon" aria-hidden>{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sg-sidebar-footer">
          <div className="sg-sidebar-user">
            <div className="sg-sidebar-avatar">{username.slice(0, 1).toUpperCase()}</div>
            <div className="sg-sidebar-user-meta">
              <div className="sg-sidebar-user-name">{username}</div>
              <div className="sg-sidebar-user-role">{roleLabel(profile.user?.role)}</div>
            </div>
          </div>
          <button
            className="sg-btn sg-btn-ghost sg-btn-sm"
            onClick={async () => {
              if (isDemo) {
                try { await api.endSelfDemoSession(); } catch { /* non-fatal */ }
                try { sessionStorage.removeItem("vt_demo_onboarding_seen"); } catch {}
              }
              await signOut(auth);
              navigate("/sign-in");
            }}
          >Sign Out</button>
        </div>
      </aside>
      <main className="sg-main-wrap">
        <TopBar />
        <div className="sg-main">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
